import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, InputAdornment,
    FormControl, InputLabel, Select, MenuItem, Grid, Card, CardContent,
    Dialog, DialogTitle, DialogContent, DialogActions, Chip,
    IconButton, Divider, CircularProgress, Alert, useTheme
} from '@mui/material';
import {
    Search as SearchIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    CalendarToday as CalendarTodayIcon,
    Event as EventIcon,
    Close as CloseIcon,
    Edit as EditIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import {
    Checkbox,
    FormControlLabel
} from '@mui/material';
import api from '../services/api';

// Função para obter cores baseadas no tema
const getPriorityConfig = (isDark) => ({
    FATAL: { 
        label: 'Fatal', 
        color: 'error', 
        borderColor: isDark ? '#ff5252' : '#d32f2f', 
        bgColor: isDark ? 'rgba(211, 47, 47, 0.15)' : '#ffebee',
        textColor: isDark ? '#ff5252' : '#d32f2f'
    },
    URGENTE: { 
        label: 'Urgente', 
        color: 'error', 
        borderColor: isDark ? '#ff6b6b' : '#ef4444', 
        bgColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2',
        textColor: isDark ? '#ff6b6b' : '#ef4444'
    },
    ALTA: { 
        label: 'Alta', 
        color: 'warning', 
        borderColor: isDark ? '#ffa726' : '#f59e0b', 
        bgColor: isDark ? 'rgba(245, 158, 11, 0.15)' : '#fef3c7',
        textColor: isDark ? '#ffa726' : '#f59e0b'
    },
    NORMAL: { 
        label: 'Normal', 
        color: 'info', 
        borderColor: isDark ? '#64b5f6' : '#3b82f6', 
        bgColor: isDark ? 'rgba(59, 130, 246, 0.15)' : '#dbeafe',
        textColor: isDark ? '#64b5f6' : '#3b82f6'
    },
    BAIXA: { 
        label: 'Baixa', 
        color: 'success', 
        borderColor: isDark ? '#66bb6a' : '#10b981', 
        bgColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#d1fae5',
        textColor: isDark ? '#66bb6a' : '#10b981'
    }
});

const getStatusConfig = (isDark) => ({
    PENDENTE: { 
        label: 'Pendente', 
        color: 'warning', 
        borderColor: isDark ? '#ffa726' : '#f59e0b', 
        bgColor: isDark ? 'rgba(245, 158, 11, 0.15)' : '#fef3c7',
        textColor: isDark ? '#ffa726' : '#f59e0b'
    },
    CONCLUIDO: { 
        label: 'Concluído', 
        color: 'success', 
        borderColor: isDark ? '#66bb6a' : '#10b981', 
        bgColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#d1fae5',
        textColor: isDark ? '#66bb6a' : '#10b981'
    },
    VENCIDO: { 
        label: 'Vencido', 
        color: 'error', 
        borderColor: isDark ? '#ff6b6b' : '#ef4444', 
        bgColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2',
        textColor: isDark ? '#ff6b6b' : '#ef4444'
    },
    CANCELADO: { 
        label: 'Cancelado', 
        color: 'error', 
        borderColor: isDark ? '#ff5252' : '#d32f2f', 
        bgColor: isDark ? 'rgba(211, 47, 47, 0.15)' : '#ffebee',
        textColor: isDark ? '#ff5252' : '#d32f2f'
    }
});

const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function CalendarioPrazos() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const [prazos, setPrazos] = useState([]);
    const [prazosParaEstatisticas, setPrazosParaEstatisticas] = useState([]); // Todos os prazos do período para estatísticas
    const [feriados, setFeriados] = useState({}); // { "2024-01-01": "Confraternização Universal", ... }
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedPrazo, setSelectedPrazo] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [dayModalOpen, setDayModalOpen] = useState(false);
    
    // Filtros
    const [busca, setBusca] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('all');
    const [filtroPrioridade, setFiltroPrioridade] = useState('all');
    const [filtroResponsavel, setFiltroResponsavel] = useState('');
    const [mostrarInativos, setMostrarInativos] = useState(false);
    const [filtroVencendoHoje, setFiltroVencendoHoje] = useState(false);
    const [filtroVenceAmanha, setFiltroVenceAmanha] = useState(false);
    
    // Listas para filtros
    const [usuarios, setUsuarios] = useState([]);
    const [partesExternas, setPartesExternas] = useState([]);

    useEffect(() => {
        loadUsuarios();
        loadPartesExternas();
    }, []);

    useEffect(() => {
        loadPrazos();
        loadFeriados(); // Carregar feriados quando mudar o mês
    }, [currentDate, busca, filtroStatus, filtroPrioridade, filtroResponsavel, mostrarInativos, filtroVencendoHoje, filtroVenceAmanha]);

    /**
     * Carrega feriados brasileiros da API BrasilAPI
     */
    const loadFeriados = async () => {
        try {
            const ano = currentDate.getFullYear();
            const response = await fetch(`https://brasilapi.com.br/api/feriados/v1/${ano}`);
            
            if (!response.ok) {
                console.warn('Erro ao carregar feriados da API:', response.status);
                return;
            }
            
            const feriadosData = await response.json();
            
            // Converter para objeto { "2024-01-01": "Confraternização Universal", ... }
            const feriadosMap = {};
            feriadosData.forEach(feriado => {
                feriadosMap[feriado.date] = feriado.name;
            });
            
            setFeriados(feriadosMap);
        } catch (error) {
            console.warn('Erro ao carregar feriados:', error);
            // Não mostra erro ao usuário, apenas loga - feriados são opcionais
        }
    };

    const loadUsuarios = async () => {
        try {
            const response = await api.get('/usuarios/lista');
            setUsuarios(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar usuários:', error);
        }
    };

    const loadPartesExternas = async () => {
        try {
            const response = await api.get('/parte-externa?ativo=true');
            setPartesExternas(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar partes externas:', error);
        }
    };

    const loadPrazos = async () => {
        setLoading(true);
        setError('');
        try {
            // Calcula período: primeiro dia do mês atual até último dia do próximo mês (igual à listagem de prazos)
            // Isso permite ver prazos futuros no calendário
            const primeiroDiaMesAtual = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
            const ultimoDiaProximoMes = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0);
            
            const formatDate = (date) => {
                const ano = date.getFullYear();
                const mes = String(date.getMonth() + 1).padStart(2, '0');
                const dia = String(date.getDate()).padStart(2, '0');
                return `${ano}-${mes}-${dia}`;
            };

            const params = new URLSearchParams();
            params.append('data_inicio', formatDate(primeiroDiaMesAtual));
            params.append('data_fim', formatDate(ultimoDiaProximoMes));
            
            // Lógica de filtros igual à listagem de prazos
            // Por padrão, mostra apenas pendentes ativos (ativo=1 e status=PENDENTE)
            // IMPORTANTE: Se filtroVencendoHoje ou filtroVenceAmanha estiverem ativos, não aplica filtro padrão de status
            if (filtroStatus && filtroStatus !== 'all') {
                // Quando há um filtro de status específico, aplica esse status
                params.append('status', filtroStatus);
                
                // Para CONCLUIDO: nunca aplica filtro de ativo (mostra todos, pois concluídos geralmente têm ativo=0)
                // Para outros status: aplica filtro de ativo apenas se mostrarInativos não estiver marcado
                if (filtroStatus !== 'CONCLUIDO' && !mostrarInativos) {
                    params.append('ativo', 'true');
                }
                // Se status é CONCLUIDO ou mostrarInativos está marcado, não aplica filtro de ativo
            } else if (filtroVencendoHoje || filtroVenceAmanha) {
                // Se está filtrando por "Vencendo Hoje" ou "Vence Amanhã", não aplica filtro padrão de status
                // Apenas aplica filtro de ativo se mostrarInativos não estiver marcado
                if (!mostrarInativos) {
                    params.append('ativo', 'true');
                }
            } else if (!mostrarInativos) {
                // Se não há filtro de status específico (é 'all') e mostrarInativos não está marcado,
                // mostra apenas pendentes ativos por padrão (igual à listagem)
                params.append('status', 'PENDENTE');
                params.append('ativo', 'true');
            }
            // Se mostrarInativos está marcado e filtroStatus é 'all', não aplica filtros de status nem ativo
            // (permite ver todos os registros incluindo inativos/finalizados)
            
            if (filtroPrioridade && filtroPrioridade !== 'all') {
                params.append('prioridade', filtroPrioridade);
            }
            
            // Filtro por responsável
            if (filtroResponsavel) {
                if (filtroResponsavel.startsWith('interno-')) {
                    const id = parseInt(filtroResponsavel.replace('interno-', ''));
                    params.append('responsavel_interno_id', id);
                } else if (filtroResponsavel.startsWith('externo-')) {
                    const id = parseInt(filtroResponsavel.replace('externo-', ''));
                    params.append('responsavel_externo_id', id);
                }
            }
            
            if (busca) {
                params.append('search', busca);
            }
            
            if (filtroVencendoHoje) {
                params.append('vencendo_hoje', 'true');
            }
            
            if (filtroVenceAmanha) {
                params.append('vence_amanha', 'true');
            }

            const response = await api.get(`/prazos?${params.toString()}`);
            setPrazos(response.data || []);
            
            // Carregar TODOS os prazos do MÊS ATUAL para estatísticas (sem filtros de status/ativo/vencendo_hoje)
            // Usar apenas o mês atual, não o próximo mês
            const ultimoDiaMesAtual = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
            const paramsEstatisticas = new URLSearchParams();
            paramsEstatisticas.append('data_inicio', formatDate(primeiroDiaMesAtual));
            paramsEstatisticas.append('data_fim', formatDate(ultimoDiaMesAtual));
            // Não aplica filtros de status, ativo ou vencendo_hoje para estatísticas (mostra total do mês)
            
            if (filtroPrioridade && filtroPrioridade !== 'all') {
                paramsEstatisticas.append('prioridade', filtroPrioridade);
            }
            
            if (filtroResponsavel) {
                if (filtroResponsavel.startsWith('interno-')) {
                    const id = parseInt(filtroResponsavel.replace('interno-', ''));
                    paramsEstatisticas.append('responsavel_interno_id', id);
                } else if (filtroResponsavel.startsWith('externo-')) {
                    const id = parseInt(filtroResponsavel.replace('externo-', ''));
                    paramsEstatisticas.append('responsavel_externo_id', id);
                }
            }
            
            if (busca) {
                paramsEstatisticas.append('search', busca);
            }
            
            try {
                const responseEstatisticas = await api.get(`/prazos?${paramsEstatisticas.toString()}`);
                setPrazosParaEstatisticas(responseEstatisticas.data || []);
            } catch (err) {
                console.error('Erro ao carregar prazos para estatísticas:', err);
                // Se falhar, usa os prazos filtrados mesmo
                setPrazosParaEstatisticas(response.data || []);
            }
        } catch (error) {
            console.error('Erro ao carregar prazos:', error);
            setError('Erro ao carregar prazos');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            // Parse direto da string "YYYY-MM-DD" sem usar new Date() para evitar timezone
            const parts = dateStr.split('-');
            if (parts.length === 3) {
                const dia = parts[2];
                const mes = parts[1];
                const ano = parts[0];
                return `${dia}/${mes}/${ano}`;
            }
            // Fallback para formato ISO se necessário
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('pt-BR');
            }
            return dateStr;
        } catch {
            return dateStr;
        }
    };

    const formatDateLong = (dateStr) => {
        if (!dateStr) return '-';
        try {
            // Parse direto da string "YYYY-MM-DD" sem usar new Date() para evitar timezone
            const parts = dateStr.split('-');
            if (parts.length === 3) {
                const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                return date.toLocaleDateString('pt-BR', { 
                    weekday: 'long',
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                });
            }
            // Fallback para formato ISO se necessário
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('pt-BR', { 
                    weekday: 'long',
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                });
            }
            return dateStr;
        } catch {
            return dateStr;
        }
    };

    const getPrazosForDate = (date) => {
        const dateStr = formatDateForAPI(date);
        return prazos.filter(p => {
            // Compara diretamente as strings de data sem conversão de timezone
            // Remove qualquer parte de hora/timezone da data_vencimento se existir
            const prazoDateStr = p.data_vencimento ? p.data_vencimento.split('T')[0].split(' ')[0] : '';
            return prazoDateStr === dateStr;
        });
    };

    /**
     * Verifica se uma data é feriado
     */
    const isFeriado = (date) => {
        const dateStr = formatDateForAPI(date);
        return feriados[dateStr] || null;
    };

    const formatDateForAPI = (date) => {
        // Usa métodos locais diretamente para evitar problemas de timezone
        const ano = date.getFullYear();
        const mes = String(date.getMonth() + 1).padStart(2, '0');
        const dia = String(date.getDate()).padStart(2, '0');
        return `${ano}-${mes}-${dia}`;
    };

    const parseDateString = (dateStr) => {
        // Parse direto da string "YYYY-MM-DD" sem usar new Date() para evitar timezone
        if (!dateStr) return null;
        const parts = dateStr.split('-');
        if (parts.length !== 3) return null;
        // Cria data usando ano, mês (0-indexed), dia diretamente
        return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    };

    const isToday = (date) => {
        const today = new Date();
        return date.getDate() === today.getDate() &&
               date.getMonth() === today.getMonth() &&
               date.getFullYear() === today.getFullYear();
    };

    const isSelected = (date) => {
        if (!selectedDate) return false;
        return date.getDate() === selectedDate.getDate() &&
               date.getMonth() === selectedDate.getMonth() &&
               date.getFullYear() === selectedDate.getFullYear();
    };

    const generateCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDay = firstDay.getDay();
        
        const days = [];
        
        // Dias do mês anterior
        const prevMonthLastDay = new Date(year, month, 0).getDate();
        for (let i = startingDay - 1; i >= 0; i--) {
            const day = prevMonthLastDay - i;
            const date = new Date(year, month - 1, day);
            days.push({ date, isCurrentMonth: false, day });
        }
        
        // Dias do mês atual
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            days.push({ date, isCurrentMonth: true, day });
        }
        
        // Dias do próximo mês para completar a grade
        const totalCells = 42; // 6 semanas
        const remainingCells = totalCells - days.length;
        for (let day = 1; day <= remainingCells; day++) {
            const date = new Date(year, month + 1, day);
            days.push({ date, isCurrentMonth: false, day });
        }
        
        return days;
    };

    const handleDateClick = (date) => {
        setSelectedDate(date);
        const dayPrazos = getPrazosForDate(date);
        if (dayPrazos.length > 0) {
            setDayModalOpen(true);
        }
    };

    const handlePrazoClick = (prazo) => {
        setSelectedPrazo(prazo);
        setModalOpen(true);
        setDayModalOpen(false);
    };

    const handleEditPrazo = () => {
        if (selectedPrazo) {
            navigate(`/prazos/editar/${selectedPrazo.id}`);
        }
    };

    const prevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const nextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const goToToday = () => {
        const today = new Date();
        setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));
        setSelectedDate(today);
    };

    const getStatistics = () => {
        // Usar TODOS os prazos do MÊS ATUAL para estatísticas (não apenas os filtrados)
        const prazosParaCalcular = prazosParaEstatisticas.length > 0 ? prazosParaEstatisticas : prazos;
        
        // Filtrar apenas prazos do mês atual visualizado (não incluir próximo mês)
        const primeiroDiaMesAtual = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const ultimoDiaMesAtual = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        const formatDateForFilter = (date) => {
            const ano = date.getFullYear();
            const mes = String(date.getMonth() + 1).padStart(2, '0');
            const dia = String(date.getDate()).padStart(2, '0');
            return `${ano}-${mes}-${dia}`;
        };
        const dataInicioMes = formatDateForFilter(primeiroDiaMesAtual);
        const dataFimMes = formatDateForFilter(ultimoDiaMesAtual);
        
        // Filtrar apenas prazos do mês atual
        const prazosMesAtual = prazosParaCalcular.filter(p => {
            if (!p.data_vencimento) return false;
            const prazoDateStr = p.data_vencimento.split('T')[0].split(' ')[0];
            return prazoDateStr >= dataInicioMes && prazoDateStr <= dataFimMes;
        });
        
        return {
            total: prazosMesAtual.length,
            pendentes: prazosMesAtual.filter(p => p.status === 'PENDENTE').length,
            // Urgentes: apenas pendentes urgentes ou fatais
            urgentes: prazosMesAtual.filter(p => 
                (p.prioridade === 'URGENTE' || p.prioridade === 'FATAL') && 
                p.status === 'PENDENTE'
            ).length,
            // Concluídos: verificar tanto CONCLUIDO quanto CONCLUÍDO (com acento)
            concluidos: prazosMesAtual.filter(p => 
                p.status === 'CONCLUIDO' || p.status === 'CONCLUÍDO'
            ).length
        };
    };

    const calendarDays = generateCalendar();
    const stats = getStatistics();

    return (
        <Box>
            {/* Header */}
            <Box 
                sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: 'primary.main', 
                    color: 'primary.contrastText',
                    borderRadius: 2,
                    boxShadow: 2
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <CalendarTodayIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    Calendário de Prazos
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                    Visualize e gerencie todos os prazos em formato de calendário
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
            )}

            {/* Controles do Calendário */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton onClick={prevMonth} size="small">
                                <ChevronLeftIcon />
                            </IconButton>
                            <Typography variant="h6" sx={{ minWidth: 200, textAlign: 'center' }}>
                                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                            </Typography>
                            <IconButton onClick={nextMonth} size="small">
                                <ChevronRightIcon />
                            </IconButton>
                            <Button 
                                variant="outlined" 
                                size="small" 
                                onClick={goToToday}
                                startIcon={<CalendarTodayIcon />}
                            >
                                Hoje
                            </Button>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TextField
                                size="small"
                                placeholder="Buscar prazos..."
                                value={busca}
                                onChange={(e) => setBusca(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ flex: 1, minWidth: 200 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filtroStatus}
                                    onChange={(e) => setFiltroStatus(e.target.value)}
                                    label="Status"
                                >
                                    <MenuItem value="all">Todos</MenuItem>
                                    <MenuItem value="PENDENTE">Pendente</MenuItem>
                                    <MenuItem value="CONCLUIDO">Concluído</MenuItem>
                                    <MenuItem value="VENCIDO">Vencido</MenuItem>
                                    <MenuItem value="CANCELADO">Cancelado</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Prioridade</InputLabel>
                                <Select
                                    value={filtroPrioridade}
                                    onChange={(e) => setFiltroPrioridade(e.target.value)}
                                    label="Prioridade"
                                >
                                    <MenuItem value="all">Todas</MenuItem>
                                    <MenuItem value="FATAL">Fatal</MenuItem>
                                    <MenuItem value="URGENTE">Urgente</MenuItem>
                                    <MenuItem value="ALTA">Alta</MenuItem>
                                    <MenuItem value="NORMAL">Normal</MenuItem>
                                    <MenuItem value="BAIXA">Baixa</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Responsável</InputLabel>
                                <Select
                                    value={filtroResponsavel}
                                    onChange={(e) => setFiltroResponsavel(e.target.value)}
                                    label="Responsável"
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {usuarios.length > 0 && (
                                        <MenuItem disabled sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                                            Internos
                                        </MenuItem>
                                    )}
                                    {usuarios.map((usuario) => (
                                        <MenuItem key={`interno-${usuario.id}`} value={`interno-${usuario.id}`}>
                                            {usuario.nome || usuario.username}
                                        </MenuItem>
                                    ))}
                                    {partesExternas.length > 0 && (
                                        <MenuItem disabled sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                                            Externos
                                        </MenuItem>
                                    )}
                                    {partesExternas.map((parte) => (
                                        <MenuItem key={`externo-${parte.id}`} value={`externo-${parte.id}`}>
                                            {parte.nome_completo || parte.nome}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={mostrarInativos}
                                        onChange={(e) => setMostrarInativos(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label="Mostrar Finalizados/Inativos"
                                sx={{ ml: 1 }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filtroVencendoHoje}
                                        onChange={(e) => setFiltroVencendoHoje(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label="Vencendo Hoje"
                                sx={{ ml: 1 }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filtroVenceAmanha}
                                        onChange={(e) => setFiltroVenceAmanha(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label="Vence Amanhã"
                                sx={{ ml: 1 }}
                            />
                        </Box>
                    </Grid>
                </Grid>

                {/* Estatísticas */}
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h4" color="primary">{stats.total}</Typography>
                                <Typography variant="body2" color="text.secondary">Total de Prazos</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h4" color="warning.main">{stats.pendentes}</Typography>
                                <Typography variant="body2" color="text.secondary">Pendentes</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h4" color="error.main">{stats.urgentes}</Typography>
                                <Typography variant="body2" color="text.secondary">Urgentes</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h4" color="success.main">{stats.concluidos}</Typography>
                                <Typography variant="body2" color="text.secondary">Concluídos</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Paper>

            {/* Calendário - Layout em Grade Tradicional */}
            <Paper sx={{ overflow: 'hidden' }}>
                {/* Cabeçalho dos dias da semana */}
                <Grid container sx={{ borderBottom: 2, borderColor: 'divider' }}>
                    {weekDays.map((day, idx) => (
                        <Grid 
                            item 
                            xs={12/7}
                            key={day} 
                            sx={{ 
                                textAlign: 'center', 
                                py: 2, 
                                backgroundColor: 'primary.main',
                                color: 'primary.contrastText',
                                fontWeight: 'bold',
                                borderRight: idx < 6 ? 1 : 0,
                                borderColor: 'divider'
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight="bold">
                                {day}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>

                {/* Dias do calendário - Grade 7x6 */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container sx={{ borderTop: 1, borderColor: 'divider' }}>
                        {calendarDays.map(({ date, isCurrentMonth, day }, index) => {
                            const dayPrazos = getPrazosForDate(date);
                            const today = isToday(date);
                            const selected = isSelected(date);
                            const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                            const feriadoNome = isFeriado(date);
                            
                            // Obter configurações de cores baseadas no tema
                            const priorityConfig = getPriorityConfig(isDarkMode);
                            const statusConfig = getStatusConfig(isDarkMode);
                            
                            return (
                                <Grid 
                                    item 
                                    xs={12/7}
                                    key={index}
                                    sx={{
                                        minHeight: { xs: 80, sm: 120, md: 140 },
                                        borderRight: index % 7 !== 6 ? 1 : 0,
                                        borderBottom: 1,
                                        borderColor: 'divider',
                                        p: { xs: 0.5, sm: 1 },
                                        cursor: 'pointer',
                                        backgroundColor: selected 
                                            ? 'action.selected' 
                                            : feriadoNome
                                                ? isDarkMode
                                                    ? 'rgba(156, 39, 176, 0.15)'
                                                    : 'rgba(156, 39, 176, 0.08)'
                                                : today 
                                                    ? isDarkMode 
                                                        ? 'rgba(25, 118, 210, 0.2)' 
                                                        : 'action.hover'
                                                    : !isCurrentMonth 
                                                        ? 'action.disabledBackground' 
                                                        : isWeekend
                                                            ? isDarkMode
                                                                ? 'rgba(255, 255, 255, 0.03)'
                                                                : 'action.hover'
                                                            : 'background.paper',
                                        opacity: !isCurrentMonth ? 0.5 : 1,
                                        borderTop: feriadoNome ? (isDarkMode ? '2px solid rgba(156, 39, 176, 0.5)' : '2px solid rgba(156, 39, 176, 0.3)') : 'none',
                                        '&:hover': {
                                            backgroundColor: selected 
                                                ? 'action.selected' 
                                                : feriadoNome
                                                    ? isDarkMode
                                                        ? 'rgba(156, 39, 176, 0.25)'
                                                        : 'rgba(156, 39, 176, 0.15)'
                                                    : isDarkMode
                                                        ? 'rgba(255, 255, 255, 0.08)'
                                                        : 'action.hover',
                                            opacity: 1
                                        },
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => handleDateClick(date)}
                                    title={feriadoNome ? `Feriado: ${feriadoNome}` : undefined}
                                >
                                    {/* Cabeçalho do dia */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: today || feriadoNome ? 'bold' : 'normal',
                                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                    color: today 
                                                        ? 'primary.main' 
                                                        : feriadoNome
                                                            ? isDarkMode
                                                                ? '#ba68c8'
                                                                : '#9c27b0'
                                                            : !isCurrentMonth 
                                                                ? 'text.disabled' 
                                                                : isWeekend
                                                                    ? isDarkMode
                                                                        ? 'text.secondary'
                                                                        : 'primary.dark'
                                                                    : 'text.primary',
                                                    backgroundColor: today 
                                                        ? isDarkMode 
                                                            ? 'rgba(25, 118, 210, 0.3)' 
                                                            : 'primary.light' 
                                                        : feriadoNome
                                                            ? isDarkMode
                                                                ? 'rgba(156, 39, 176, 0.3)'
                                                                : 'rgba(156, 39, 176, 0.15)'
                                                            : 'transparent',
                                                    borderRadius: '50%',
                                                    width: { xs: 20, sm: 24 },
                                                    height: { xs: 20, sm: 24 },
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {day}
                                            </Typography>
                                            {feriadoNome && (
                                                <Box
                                                    sx={{
                                                        width: { xs: 4, sm: 6 },
                                                        height: { xs: 4, sm: 6 },
                                                        borderRadius: '50%',
                                                        bgcolor: isDarkMode ? '#ba68c8' : '#9c27b0',
                                                        flexShrink: 0
                                                    }}
                                                    title={feriadoNome}
                                                />
                                            )}
                                        </Box>
                                        {dayPrazos.length > 0 && (() => {
                                            // Determinar cor do badge baseado na prioridade/status
                                            let badgeColor = '#3b82f6'; // Normal padrão
                                            if (dayPrazos.some(p => p.status === 'CANCELADO' || p.status === 'VENCIDO')) {
                                                badgeColor = isDarkMode ? '#ff5252' : '#d32f2f';
                                            } else if (dayPrazos.some(p => p.prioridade === 'FATAL')) {
                                                badgeColor = isDarkMode ? '#ff5252' : '#d32f2f';
                                            } else if (dayPrazos.some(p => p.prioridade === 'URGENTE')) {
                                                badgeColor = isDarkMode ? '#ff6b6b' : '#ef4444';
                                            } else if (dayPrazos.some(p => p.prioridade === 'ALTA')) {
                                                badgeColor = isDarkMode ? '#ffa726' : '#f59e0b';
                                            } else {
                                                badgeColor = isDarkMode ? '#64b5f6' : '#3b82f6';
                                            }
                                            
                                            return (
                                                <Chip 
                                                    label={dayPrazos.length} 
                                                    size="small"
                                                    sx={{ 
                                                        height: { xs: 16, sm: 20 },
                                                        fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                                        bgcolor: badgeColor,
                                                        color: 'white',
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                            );
                                        })()}
                                    </Box>
                                    
                                    {/* Lista de prazos do dia */}
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        gap: 0.5,
                                        maxHeight: { xs: 60, sm: 100 },
                                        overflowY: 'auto',
                                        '&::-webkit-scrollbar': {
                                            width: '4px',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'divider',
                                            borderRadius: '2px',
                                        }
                                    }}>
                                        {dayPrazos.slice(0, isCurrentMonth ? 3 : 0).map((prazo) => {
                                            const priority = priorityConfig[prazo.prioridade] || priorityConfig.NORMAL;
                                            const status = statusConfig[prazo.status] || statusConfig.PENDENTE;
                                            // Usa cor do status se for CANCELADO ou VENCIDO, senão usa prioridade
                                            const borderColor = (prazo.status === 'CANCELADO' || prazo.status === 'VENCIDO') 
                                                ? status.borderColor 
                                                : priority.borderColor;
                                            const bgColor = (prazo.status === 'CANCELADO' || prazo.status === 'VENCIDO')
                                                ? status.bgColor
                                                : priority.bgColor;
                                            const textColor = (prazo.status === 'CANCELADO' || prazo.status === 'VENCIDO')
                                                ? status.textColor
                                                : priority.textColor;
                                            
                                            return (
                                                <Box
                                                    key={prazo.id}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePrazoClick(prazo);
                                                    }}
                                                    sx={{
                                                        p: { xs: 0.3, sm: 0.5 },
                                                        borderRadius: 0.5,
                                                        bgcolor: bgColor,
                                                        borderLeft: 3,
                                                        borderColor: borderColor,
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            opacity: isDarkMode ? 0.8 : 0.9,
                                                            transform: 'translateX(2px)',
                                                            boxShadow: isDarkMode ? 2 : 1,
                                                            bgcolor: isDarkMode 
                                                                ? (prazo.status === 'CANCELADO' || prazo.status === 'VENCIDO')
                                                                    ? 'rgba(211, 47, 47, 0.25)'
                                                                    : 'rgba(59, 130, 246, 0.25)'
                                                                : bgColor
                                                        },
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            display: 'block',
                                                            fontWeight: 'medium',
                                                            fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                                            color: isDarkMode ? textColor : textColor,
                                                            lineHeight: 1.2
                                                        }}
                                                        noWrap
                                                    >
                                                        {prazo.cliente_nome?.split(' ')[0] || 'Sem cliente'}
                                                    </Typography>
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            display: 'block',
                                                            fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                            color: isDarkMode ? 'text.secondary' : 'text.secondary',
                                                            lineHeight: 1.2,
                                                            opacity: isDarkMode ? 0.8 : 1
                                                        }}
                                                        noWrap
                                                    >
                                                        {prazo.instrucoes?.substring(0, 18) || 'Sem instruções'}...
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                        {dayPrazos.length > 3 && isCurrentMonth && (
                                            <Typography 
                                                variant="caption" 
                                                color="text.secondary" 
                                                sx={{ 
                                                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                    fontStyle: 'italic',
                                                    textAlign: 'center',
                                                    mt: 0.5,
                                                    opacity: isDarkMode ? 0.7 : 1
                                                }}
                                            >
                                                +{dayPrazos.length - 3} mais
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Paper>

            {/* Legenda */}
            <Paper sx={{ p: 2, mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>Legenda:</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box>
                        <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                            Prioridades:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {(() => {
                                const priorityConfig = getPriorityConfig(isDarkMode);
                                return Object.values(priorityConfig).map((p) => (
                                    <Chip 
                                        key={p.label}
                                        label={p.label} 
                                        size="small" 
                                        sx={{ 
                                            bgcolor: p.bgColor, 
                                            color: p.textColor, 
                                            border: `1px solid ${p.borderColor}`,
                                            fontWeight: 'medium'
                                        }} 
                                    />
                                ));
                            })()}
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                            Status:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {(() => {
                                const statusConfig = getStatusConfig(isDarkMode);
                                return Object.values(statusConfig).map((s) => (
                                    <Chip 
                                        key={s.label}
                                        label={s.label} 
                                        size="small" 
                                        sx={{ 
                                            bgcolor: s.bgColor, 
                                            color: s.textColor, 
                                            border: `1px solid ${s.borderColor}`,
                                            fontWeight: 'medium'
                                        }} 
                                    />
                                ));
                            })()}
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                            Outros:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: isDarkMode ? '#ba68c8' : '#9c27b0'
                                    }}
                                />
                                <Typography variant="caption">Feriado Nacional</Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Paper>

            {/* Modal de Prazos do Dia */}
            <Dialog open={dayModalOpen} onClose={() => setDayModalOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h6">
                                Prazos para {selectedDate ? formatDateLong(formatDateForAPI(selectedDate)) : ''}
                            </Typography>
                            {selectedDate && isFeriado(selectedDate) && (
                                <Chip
                                    label={`🎉 ${isFeriado(selectedDate)}`}
                                    size="small"
                                    sx={{
                                        mt: 1,
                                        bgcolor: isDarkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                                        color: isDarkMode ? '#ba68c8' : '#9c27b0',
                                        border: `1px solid ${isDarkMode ? 'rgba(186, 104, 200, 0.5)' : 'rgba(156, 39, 176, 0.3)'}`,
                                        fontWeight: 'medium'
                                    }}
                                />
                            )}
                        </Box>
                        <IconButton onClick={() => setDayModalOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedDate && getPrazosForDate(selectedDate).map((prazo) => {
                        const priorityConfig = getPriorityConfig(isDarkMode);
                        const statusConfig = getStatusConfig(isDarkMode);
                        const priority = priorityConfig[prazo.prioridade] || priorityConfig.NORMAL;
                        const status = statusConfig[prazo.status] || statusConfig.PENDENTE;
                        // Usa cor do status se for CANCELADO ou VENCIDO, senão usa prioridade
                        const borderColor = (prazo.status === 'CANCELADO' || prazo.status === 'VENCIDO')
                            ? status.borderColor
                            : priority.borderColor;
                        const bgColor = (prazo.status === 'CANCELADO' || prazo.status === 'VENCIDO')
                            ? status.bgColor
                            : priority.bgColor;
                        const textColor = (prazo.status === 'CANCELADO' || prazo.status === 'VENCIDO')
                            ? status.textColor
                            : priority.textColor;
                        
                        return (
                            <Card 
                                key={prazo.id} 
                                sx={{ 
                                    mb: 2, 
                                    cursor: 'pointer',
                                    borderLeft: 4,
                                    borderColor: borderColor,
                                    bgcolor: bgColor,
                                    '&:hover': {
                                        opacity: isDarkMode ? 0.8 : 0.9,
                                        transform: 'translateX(2px)',
                                        boxShadow: isDarkMode ? 3 : 2,
                                        bgcolor: isDarkMode 
                                            ? (prazo.status === 'CANCELADO' || prazo.status === 'VENCIDO')
                                                ? 'rgba(211, 47, 47, 0.25)'
                                                : 'rgba(59, 130, 246, 0.25)'
                                            : bgColor
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                                onClick={() => handlePrazoClick(prazo)}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {prazo.cliente_nome || 'Sem cliente'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {prazo.numero_processo || '-'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Chip 
                                                label={priority.label}
                                                size="small"
                                                sx={{
                                                    bgcolor: priority.bgColor,
                                                    color: priority.textColor,
                                                    border: `1px solid ${priority.borderColor}`,
                                                    fontWeight: 'medium'
                                                }}
                                            />
                                            <Chip 
                                                label={status.label}
                                                size="small"
                                                sx={{
                                                    bgcolor: status.bgColor,
                                                    color: status.textColor,
                                                    border: `1px solid ${status.borderColor}`,
                                                    fontWeight: 'medium'
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Instruções:</strong> {prazo.instrucoes || '-'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Vara:</strong> {prazo.vara || '-'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Responsável:</strong> {prazo.responsavel_interno_nome || prazo.responsavel_externo_nome || '-'}
                                    </Typography>
                                </CardContent>
                            </Card>
                        );
                    })}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDayModalOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            {/* Modal de Detalhes do Prazo */}
            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            Detalhes do Prazo
                        </Typography>
                        <IconButton onClick={() => setModalOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedPrazo && (
                        <Box sx={{ mt: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Cliente</Typography>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        {selectedPrazo.cliente_nome || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Número do Processo</Typography>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        {selectedPrazo.numero_processo || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Data do Prazo</Typography>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        {formatDate(selectedPrazo.data_vencimento)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Data de Criação</Typography>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        {formatDate(selectedPrazo.data_criacao)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Prioridade</Typography>
                                    <Box sx={{ mb: 2 }}>
                                        {(() => {
                                            const priorityConfig = getPriorityConfig(isDarkMode);
                                            const priority = priorityConfig[selectedPrazo.prioridade] || priorityConfig.NORMAL;
                                            return (
                                                <Chip 
                                                    label={priority.label}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: priority.bgColor,
                                                        color: priority.textColor,
                                                        border: `1px solid ${priority.borderColor}`,
                                                        fontWeight: 'medium'
                                                    }}
                                                />
                                            );
                                        })()}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Status</Typography>
                                    <Box sx={{ mb: 2 }}>
                                        {(() => {
                                            const statusConfig = getStatusConfig(isDarkMode);
                                            const status = statusConfig[selectedPrazo.status] || statusConfig.PENDENTE;
                                            return (
                                                <Chip 
                                                    label={status.label}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: status.bgColor,
                                                        color: status.textColor,
                                                        border: `1px solid ${status.borderColor}`,
                                                        fontWeight: 'medium'
                                                    }}
                                                />
                                            );
                                        })()}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Vara</Typography>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        {selectedPrazo.vara || '-'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Responsável Interno</Typography>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                        {selectedPrazo.responsavel_interno_nome || '-'}
                                    </Typography>
                                </Grid>
                                {selectedPrazo.responsavel_externo_nome && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Responsável Externo</Typography>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {selectedPrazo.responsavel_externo_nome}
                                        </Typography>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">Instruções</Typography>
                                    <Paper sx={{ 
                                        p: 2, 
                                        mt: 1, 
                                        bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'grey.50',
                                        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'
                                    }}>
                                        <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                            {selectedPrazo.instrucoes || '-'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                {selectedPrazo.observacoes && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Observações</Typography>
                                        <Paper sx={{ 
                                            p: 2, 
                                            mt: 1, 
                                            bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'grey.50',
                                            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'
                                        }}>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                                                {selectedPrazo.observacoes}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                )}
                                {selectedPrazo.data_conclusao && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Data de Conclusão</Typography>
                                        <Typography variant="body1">
                                            {formatDate(selectedPrazo.data_conclusao)}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalOpen(false)}>Fechar</Button>
                    <Button 
                        variant="contained" 
                        startIcon={<EditIcon />}
                        onClick={handleEditPrazo}
                    >
                        Editar Prazo
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default CalendarioPrazos;
